import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
// import { mockApi } from '../services/mockData';
import { rebateApi, allHistoryApi } from '../services/api';
import type { Rebate, Withdrawal, Transaction } from '../types';
import { useTranslation } from '../hooks/useTranslation';
import UserProfileCard from '../components/dashboard/UserProfileCard';
import QuickActions from '../components/dashboard/QuickActions';
import StatisticsCards from '../components/dashboard/StatisticsCards';
import RecentTransactions from '../components/dashboard/RecentTransactions';
import DashboardSkeleton from '../components/skeletons/DashboardSkeleton';

const Dashboard = () => {
  const { user, updateUser } = useAuthStore();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [recentRebates, setRecentRebates] = useState<Rebate[]>([]);
  const [recentWithdrawals, setRecentWithdrawals] = useState<Withdrawal[]>([]);
  const [recentTransactions, setRecentTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ totalRebate: 0, pendingRebate: 0, balance: 0 });

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    if (!user) return;
    
    try {
      const [allHistory, rebateStats, userProfile] = await Promise.all([
        allHistoryApi.getHistory(1, 3), // Get 3 most recent transactions (already sorted by backend)
        rebateApi.getStats(),
        (await import('../services/api')).userApi.getProfile(),
      ]);
      
      // Separate the sorted transactions into rebates, withdrawals, and transactions
      const recentRebatesList: Rebate[] = [];
      const recentWithdrawalsList: Withdrawal[] = [];
      const recentTransactionsList: Transaction[] = [];
      
      allHistory.items?.forEach((item: any) => {
        if (item.type === 'rebate') {
          recentRebatesList.push({
            id: item.id,
            userId: user.id,
            exchangeAccountId: '',
            exchange: item.exchange,
            amount: item.amount,
            description: item.description,
            status: item.status,
            createdAt: item.createdAt,
          });
        } else if (item.type === 'withdrawal') {
          recentWithdrawalsList.push({
            id: item.id,
            userId: user.id,
            amount: item.amount,
            network: item.network,
            walletAddress: item.walletAddress,
            status: item.status,
            txHash: item.txHash,
            claimStatus: item.claimStatus,
            contractAddress: item.contractAddress,
            claimTxHash: item.claimTxHash,
            createdAt: item.createdAt,
            processedAt: item.processedAt,
          });
        } else if (item.type === 'transaction') {
          recentTransactionsList.push({
            id: item.id,
            userId: user.id,
            type: item.transactionType as Transaction['type'],
            amount: item.amount,
            description: item.description,
            status: item.status,
            createdAt: item.createdAt,
            transactionType: item.transactionType, // Keep transactionType for display
          } as Transaction & { transactionType: string });
        }
      });
      
      setRecentRebates(recentRebatesList);
      setRecentWithdrawals(recentWithdrawalsList);
      setRecentTransactions(recentTransactionsList);
      setStats(rebateStats);
      
      // Update user data from API
      updateUser({
        balance: userProfile.balance || 0,
        pendingRebate: userProfile.pendingRebate || 0,
        points: userProfile.points || 0,
      });
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (loading) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="p-4 pb-24">
      <UserProfileCard
        user={user}
        formatCurrency={formatCurrency}
        balanceLabel={t('dashboard.balance')}
        subtitle={t('dashboard.subtitle')}
      />

      <QuickActions onNavigate={navigate} />

      <StatisticsCards
        totalVolume={0}
        totalRebate={stats.totalRebate}
        pendingRebate={stats.pendingRebate}
        formatCurrency={formatCurrency}
        labels={{
          totalVolume: t('dashboard.balance'),
          totalRebate: t('rebate.totalRebate'),
          pendingRebate: t('rebate.pendingRebate'),
        }}
      />

      <RecentTransactions
        rebates={recentRebates}
        withdrawals={recentWithdrawals}
        transactions={recentTransactions}
        formatCurrency={formatCurrency}
        onViewAll={() => navigate('/history')}
        viewAllLabel={t('common.viewAll')}
        noTransactionsText={t('history.noTransactions')}
      />

      {/* Promotional Banner */}
      {/* <div className="bg-gradient-to-r from-primary/20 to-primary/10 rounded-xl p-4 mb-6 border border-primary/30">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="text-xl font-bold text-white mb-1">GET 5 USDT BONUS</div>
            <div className="text-xs text-tg-hint">When successfully activating rebates with the first exchange</div>
          </div>
          <button className="bg-primary text-black px-4 py-2 rounded-lg font-semibold text-sm">
            Get Now →
          </button>
        </div>
      </div> */}
    </div>
  );
};

export default Dashboard;
